import type { PillColor } from '@wasabi/actions'

import type { DeployState } from '@/services/my-work/my-work.types'

/** "Blocked" means a person or a decision must act; anything else not ready is "Waiting on" its next step. */
export type WorkStatusKind =
  | 'blocked'
  | 'waiting'
  | 'in-progress'
  | 'ready'
  | 'passed'
  | 'failed'
  | 'to-do'
  | 'cancelled'

export type WaitReason = 'merge' | 'deploy' | 'qa'

export interface WorkStatus {
  kind: WorkStatusKind
  label: string
  color: PillColor
  /** One sentence for a tooltip: what it waits on, or who must act. */
  help: string
}

const WAIT_LABELS: Record<WaitReason, string> = {
  merge: 'Waiting on merge',
  deploy: 'Waiting on deploy',
  qa: 'Waiting on QA'
}

const WAIT_HELP: Record<WaitReason, string> = {
  merge: 'Its pull requests are not merged yet.',
  deploy: 'Merged, and waiting for the fix to reach the environment it is tested in.',
  qa: 'Deployed, and waiting for QA to test it.'
}

export const WORK_STATUS_COLORS: Record<WorkStatusKind, PillColor> = {
  blocked: 'red',
  waiting: 'gray',
  'in-progress': 'blue',
  ready: 'green',
  passed: 'green',
  failed: 'red',
  'to-do': 'gray',
  cancelled: 'gray'
}

/** Blocked, or failed QA and back with its developer: both wait on a person, so both count as Blocked. */
export function needsAPerson(status: WorkStatus): boolean {
  return status.kind === 'blocked' || status.kind === 'failed'
}

export function blockedStatus(by = 'a person or a decision'): WorkStatus {
  return { kind: 'blocked', label: 'Blocked', color: 'red', help: `Waiting on ${by}.` }
}

export function waitingStatus(reason: WaitReason): WorkStatus {
  return { kind: 'waiting', label: WAIT_LABELS[reason], color: 'gray', help: WAIT_HELP[reason] }
}

function status(kind: WorkStatusKind, label: string, help: string): WorkStatus {
  return { kind, label, color: WORK_STATUS_COLORS[kind], help }
}

type QaGroup = 'ready' | 'waitingDeploy' | 'inProgress' | 'blocked'

/** A QA queue row: its group is already the fleet's verdict on where it stands. */
export function qaQueueStatus(ticket: { group: QaGroup; waitingOn?: string; lease?: { who: string } }): WorkStatus {
  if (ticket.group === 'blocked') return blockedStatus(ticket.waitingOn)
  if (ticket.group === 'waitingDeploy') return waitingStatus('deploy')
  if (ticket.group === 'inProgress')
    return status('in-progress', 'In progress', `${ticket.lease?.who ?? 'Someone'} is testing it.`)
  return status('ready', 'Ready to test', 'Its fix is deployed where it is tested, and nobody holds it.')
}

type DeployStateLike = DeployState | 'failed' | 'not-released'

export interface TicketStatusInput {
  /** The Jira status. */
  status: string
  deploy?: { dev?: { state: DeployStateLike }; staging?: { state: DeployStateLike } }
  merged?: boolean
}

const DECISION_STATUS = /blocked|decision/i
const REVIEW_STATUSES = new Set(['PR Review', 'Code Review', 'In Review'])

/** QA's progress in one environment, as the status vocabulary. */
function envStatus(state: DeployStateLike | undefined, env: 'dev' | 'staging'): WorkStatus | null {
  switch (state) {
    case 'verified':
      return status('passed', `QA passed in ${env}`, `QA passed it in ${env}.`)
    case 'failed':
      return status('failed', 'Failed QA', `QA failed it in ${env}.`)
    case 'cant-verify':
      return blockedStatus('a person: QA could not verify it')
    case 'staged':
      return blockedStatus("a person to post Claude's QA result")
    case 'deployed':
      return waitingStatus('qa')
    case 'waiting':
      return waitingStatus('deploy')
    default:
      return null
  }
}

/** A ticket from its Jira status and, where known, its deploy and QA facts: Releases, My work, ticket page. */
export function ticketStatus(ticket: TicketStatusInput): WorkStatus {
  const { status: jira } = ticket
  if (jira === 'Cancelled') return status('cancelled', 'Cancelled', 'Cancelled, so it ships with nothing.')
  if (DECISION_STATUS.test(jira)) return blockedStatus('a decision on the ticket')
  if (jira === 'Failed QA') return status('failed', 'Failed QA', 'QA failed it; it goes back to its developer.')
  if (jira === 'Done in Production') return status('passed', 'Done in production', 'Verified in production.')
  if (jira === 'Done in Staging') return status('passed', 'QA passed in staging', 'QA passed it in staging.')
  if (jira === 'Done') return envStatus(ticket.deploy?.staging?.state, 'staging') ?? waitingStatus('qa')
  if (jira === 'Testing' || jira === 'Ready for Testing')
    return envStatus(ticket.deploy?.dev?.state, 'dev') ?? waitingStatus('qa')
  if (jira === 'In Progress') return status('in-progress', 'In progress', 'Someone is working on it.')
  if (REVIEW_STATUSES.has(jira)) return ticket.merged ? waitingStatus('deploy') : waitingStatus('merge')
  if (jira === 'To Do') return status('to-do', 'To do', 'Nobody has started it.')
  return status('to-do', jira, `Jira says ${jira}.`)
}

/** What the fleet sends in place of a name the viewer may not see. */
const HIDDEN_NAMES = new Set(['(hidden)', 'a staff member', 'hidden'])

export const HIDDEN_PERSON_LABEL = 'A staff member'
export const HIDDEN_PERSON_HELP = 'Fleet operators see names here.'

export function isHiddenPerson(name: string | null | undefined): boolean {
  return Boolean(name && HIDDEN_NAMES.has(name.trim().toLowerCase()))
}

/** A name to show: the person's, or "A staff member" when the viewer may not see it. */
export function personLabel(name: string | null | undefined): string {
  if (!name) return ''
  return isHiddenPerson(name) ? HIDDEN_PERSON_LABEL : name
}
